// File: client/src/platform/RulebookModal.js
//
// Generic rulebook renderer. Given any variant's rulebook (see rulebookContract),
// it renders the sections in a fixed order. No variant ships its own rulebook UI.
// Used both from the setup modal (before a match) and in-game (via the host's
// "How to Play" button).

import React from 'react';
import { X, BookOpen } from 'lucide-react';
import { RULEBOOK_SECTIONS } from './rulebookContract';

// Render one section based on its declared type; returns null if the variant didn't
// provide that section (so the layout only shows what exists).
function Section({ label, type, value }) {
  if (value == null) return null;
  if (Array.isArray(value) && value.length === 0) return null;

  return (
    <div className="mb-5">
      <h3 className="text-sm font-bold text-fuchsia-300 uppercase tracking-wide mb-2">{label}</h3>
      {type === 'text' && <p className="text-slate-200 text-sm leading-relaxed">{value}</p>}

      {type === 'orderedList' && (
        <ol className="list-decimal list-inside space-y-1 text-slate-200 text-sm">
          {value.map((item, i) => <li key={i}>{item}</li>)}
        </ol>
      )}

      {type === 'list' && (
        <ul className="list-disc list-inside space-y-1 text-slate-200 text-sm">
          {value.map((item, i) => <li key={i}>{item}</li>)}
        </ul>
      )}

      {type === 'sections' && (
        <div className="space-y-3">
          {value.map((sec, i) => (
            <div key={i} className="bg-white/5 rounded-lg p-3">
              <p className="text-slate-100 font-semibold text-sm mb-1">{sec.title}</p>
              {sec.body && <p className="text-slate-300 text-sm leading-relaxed">{sec.body}</p>}
              {Array.isArray(sec.items) && (
                <ul className="list-disc list-inside space-y-0.5 text-slate-300 text-sm mt-1">
                  {sec.items.map((it, j) => <li key={j}>{it}</li>)}
                </ul>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const RulebookModal = ({ variantName, rulebook, onClose }) => {
  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div
        className="bg-slate-800 rounded-xl w-full max-w-lg max-h-[85vh] overflow-hidden shadow-2xl border border-slate-700 flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center p-5 border-b border-slate-700">
          <h2 className="text-lg font-bold text-white flex items-center gap-2">
            <BookOpen size={18} className="text-fuchsia-400" /> {variantName} — Rulebook
          </h2>
          <button onClick={onClose} className="text-slate-400 hover:text-white transition">
            <X size={20} />
          </button>
        </div>
        <div className="p-5 overflow-y-auto">
          {RULEBOOK_SECTIONS.map(({ key, label, type }) => (
            <Section key={key} label={label} type={type} value={rulebook[key]} />
          ))}
        </div>
      </div>
    </div>
  );
};

export default RulebookModal;
